import { Chart, useChart } from '@chakra-ui/charts';
import { Box, Skeleton, Stack } from '@chakra-ui/react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { FaceliftScrollContainer } from '@/components/Atoms/FaceliftScrollContainer';
import FaceliftText from '@/components/Atoms/FaceliftText/FaceliftText';
import Show from '@/components/Molecules/Show';
import { useColorMode } from '@/components/ui/color-mode-utils';
import { colors } from '@/components/ui/theme';
import { formatChartNumber } from '@/utils';
import type { GroupedBarChartProps } from './GroupedBarChart.types';

interface TooltipPayloadItem {
  name: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  disabledSeries: Set<string>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, disabledSeries }) => {
  if (!active || !payload) return null;

  // Filter out disabled series from the tooltip
  const filteredPayload = payload.filter((entry) => {
    return !disabledSeries.has(String(entry.name));
  });

  if (filteredPayload.length === 0) return null;

  const reversedPayload = [...filteredPayload].reverse();
  
  return (
    <Box
      p={3}
      borderRadius="md"
      borderWidth="1px" 
      boxShadow="lg" 
      _dark={{ bg: 'darkModeBgColor', borderColor: 'gray.600' }} 
      _light={{ bg: 'white', borderColor: 'gray.200' }}
    >
      <FaceliftText fontSize="sm" fontWeight="semibold" mb={2} _dark={{ color: 'white' }} _light={{ color: 'gray.800' }}>
        {label}
      </FaceliftText>
      <Stack gap={1}>
        {reversedPayload.map((entry) => (
          <Stack key={`${label}-${entry.name}`} direction="row" align="center" gap={2}>
            <Box width={2} height={2} borderRadius="full" bg={entry.color} />
            <FaceliftText fontSize="xs" _dark={{ color: 'gray.300' }} _light={{ color: 'gray.600' }}>
              {entry.name}: {entry.value?.toLocaleString()}
            </FaceliftText>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const GroupedBarChart: React.FC<GroupedBarChartProps> = ({
  title = '',
  data: propData,
  isLoading = false,
  ...rest
}) => {
  // Default color palette for series
  const defaultColors = [
    'brand.500',
    'axolotlBlue',
    'traidPink',
    'nvisionPurple',
    'imbedGray',
    'brand.600',
    'statusTeal',
    'brand.300',
    'brand.200',
    'creamBeige',
    'lightBlue',
    'defaultGray',
  ];

  // Transform the nested data structure into flat format and derive series
  const { transformedData, series } = useMemo(() => {
    if (!propData || propData.length === 0) {
      return { transformedData: [], series: [] };
    }

    // Extract all unique titles (series names) from the data
    const titlesSet = new Set<string>();
    propData.forEach((category) => {
      category.data.forEach((item) => {
        titlesSet.add(item.title);
      });
    });

    const titles = Array.from(titlesSet);

    // Create series configuration with colors
    const seriesConfig = titles.map((title, index) => ({
      name: title,
      color: defaultColors[index % defaultColors.length],
    }));

    // Transform data into flat structure
    const flatData = propData.map((category) => {
      const dataPoint: Record<string, string | number> = { month: category.category };

      // Add each series value to the data point
      category.data.forEach((item) => {
        dataPoint[item.title] = item.value;
      });

      // Fill in missing series with 0
      titles.forEach((title) => {
        if (!(title in dataPoint)) {
          dataPoint[title] = 0;
        }
      });

      return dataPoint;
    });

    return { transformedData: flatData, series: seriesConfig };
  }, [propData]);

  // State to track disabled series
  const [disabledSeries, setDisabledSeries] = useState<Set<string>>(new Set());

  const chart = useChart({ data: transformedData, series });
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  // Check if all series are disabled
  const allSeriesDisabled = series?.every(s => disabledSeries.has(String(s.name))) || false;

  // Toggle series visibility
  const handleLegendClick = (seriesName: string) => {
    setDisabledSeries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(seriesName)) {
        newSet.delete(seriesName);
      } else {
        newSet.add(seriesName);
      }
      return newSet;
    });
  };

  return (
    <>
      <Show when={isLoading}>
        <Box width="100%" height="310px" borderRadius="5px" borderWidth="1px" borderColor={'gray.400'} p={3} pl={2} _dark={{ bg: 'darkModeBgColor' }} _light={{ bg: 'white' }} overflow="hidden" display="flex" flexDirection="column" {...rest}>
          <Skeleton height="20px" width="60%" maxW="240px" mb={2} ml={4} _dark={{ bg: 'gray.700' }} _light={{ bg: 'gray.200' }} />
          <Box flex="1" px={2} py={2} display="flex" alignItems="flex-end" justifyContent="center" gap={2} height="220px">
            {Array.from({ length: 6 }, (_, groupIndex) => (
              <Box
                // biome-ignore lint/suspicious/noArrayIndexKey: Temporary skeleton loader
                key={groupIndex}
                display="flex"
                alignItems="flex-end"
                justifyContent="center"
                gap={1}
                height="100%"
                width="48px"
              >
                {[
                  40 + (groupIndex % 5) * 5,
                  47 + (groupIndex % 5) * 4,
                  35 + (groupIndex % 5) * 6,
                ].map((heightPercent, idx) => (
                  <Skeleton
                    // biome-ignore lint/suspicious/noArrayIndexKey: Temporary skeleton loader
                    key={idx}
                    height={`${heightPercent}%`}
                    width="10px"
                    borderRadius="2px 2px 0 0"
                    _dark={{ bg: 'gray.700' }}
                    _light={{ bg: 'gray.200' }}
                  />
                ))}
              </Box>
            ))}
          </Box>
          <Stack direction="row" justify="center" gap={3} mt={2} flexWrap="wrap">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton
                // biome-ignore lint/suspicious/noArrayIndexKey: Temporary skeleton loader
                key={i}
                height="16px"
                width="100px"
                _dark={{ bg: 'gray.700' }}
                _light={{ bg: 'gray.200' }}
              />
            ))}
          </Stack>
        </Box>
      </Show>

      <Show when={!isLoading}>
        <Box width="100%" height="310px" borderRadius="5px" borderWidth="1px" borderColor={'gray.400'} p={3} pl={2} _dark={{ bg: 'darkModeBgColor' }} _light={{ bg: 'white' }} overflow="visible" display="flex" flexDirection="column" {...rest}>
          <Show when={!!title}>
            <FaceliftText fontSize="md" fontWeight="semibold" mb={2} mt={1} ml={4} _dark={{ color: 'white' }} _light={{ color: 'black' }}>
              {title}
            </FaceliftText>
          </Show>
          <Box position="relative" height="calc(100% - 50px)">
            <Show when={allSeriesDisabled}>
              <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={10}>
                <FaceliftText fontSize="md" fontWeight="semibold" _dark={{ color: 'gray.400' }} _light={{ color: 'gray.500' }}>
                  No data found
                </FaceliftText>
              </Box>
            </Show>
            <FaceliftScrollContainer height="100%" minWidth="666px" pb={0} style={{ overflow: 'visible' }}>
              <Box position="relative" height="100%" width="100%" display="flex" flexDirection="row" overflow="visible" style={{ overflow: 'visible' }}>
                <Chart.Root height="100%" width="100%" chart={chart}>
                  <BarChart data={chart.data} margin={{ top: 14, right: 10, left: -8, bottom: 2 }} barCategoryGap="35%" barGap={3.5} width={536} height={260}>
                    <Show when={!allSeriesDisabled}>
                      <CartesianGrid strokeDasharray="0" vertical={false} stroke={colors.robotGrey} />
                    </Show>
                    <Show when={!allSeriesDisabled}>
                      <XAxis
                        dataKey={chart.key('month')}
                        axisLine={{ stroke: isDark ? colors.lightGray : colors.black, strokeWidth: 1 }}
                        tickLine={false}
                        tick={{
                          style: {
                            fill: isDark ? colors.lightGray : colors.black,
                            fontSize: '12px',
                            fontWeight: 500,
                            lineHeight: '100%',
                            letterSpacing: '1%',
                            textAlign: 'center',
                            textTransform: 'capitalize',
                          },
                        }}
                      />
                    </Show>
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={!allSeriesDisabled ? {
                        style: {
                          fill: isDark ? colors.lightGray : colors.black,
                          fontSize: '10px',
                          fontWeight: 500,
                          lineHeight: '100%',
                          letterSpacing: '1%',
                          textAlign: 'right',
                          textTransform: 'capitalize',
                        },
                      } : false}
                      tickFormatter={!allSeriesDisabled ? formatChartNumber : undefined}
                      // domain={!allSeriesDisabled ? [0, 200] : [0, 'auto']}
                      // ticks={!allSeriesDisabled ? [0, 50, 100, 150, 200, 400, 800, 1000] : []} need to change as per requirements of other components
                    />
                    <Show when={!allSeriesDisabled}>
                      <Tooltip
                        cursor={false}
                        animationDuration={100}
                        content={<CustomTooltip disabledSeries={disabledSeries} />}
                        allowEscapeViewBox={{ x: false, y: true }}
                        wrapperStyle={{
                          zIndex: 1000,
                          pointerEvents: 'none',
                          maxWidth: '300px',
                          overflow: 'visible',
                        }}
                        offset={10}
                      />
                    </Show>
                    {[...chart.series].reverse().map((item) => {
                      const isDisabled = disabledSeries.has(String(item.name));
                      return (
                        <Show key={String(item.name)} when={!isDisabled}>
                          <Bar dataKey={chart.key(String(item.name))} fill={chart.color(item.color)} radius={[2, 2, 0, 0]} barSize={9.5} />
                        </Show>
                      );
                    })}
                  </BarChart>
                </Chart.Root>
                {/* Legend on the right */}
                <Box width="120px" display="flex" alignItems="flex-start" justifyContent="flex-start" pl={2} ml={2} height="100%">
                  <Box
                    maxHeight="100%"
                    overflowY="auto"
                    overflowX="hidden"
                    width="100%"
                    pr={1}
                    css={{
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: isDark ? '#4B5563' : '#9CA3AF',
                        borderRadius: '3px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: '#6B7280',
                      },
                    }}
                  >
                    <Stack 
                      direction="column" 
                      justify="flex-start" 
                      align="flex-start" 
                      gap={2}
                    >
                      {series.map((s) => {
                        const seriesName = String(s.name);
                        const isDisabled = disabledSeries.has(seriesName);
                        return (
                          <Stack
                            key={seriesName}
                            direction="row"
                            align="center"
                            gap={1.5}
                            cursor="pointer"
                            onClick={() => handleLegendClick(seriesName)}
                            opacity={isDisabled ? 0.4 : 1}
                            transition="opacity 0.2s"
                            _hover={{ opacity: isDisabled ? 0.6 : 0.8 }}
                          >
                            <Box width={2.5} height={2.5} borderRadius="full" bg={chart.color(s.color)} />
                            <FaceliftText
                              fontSize="2xs"
                              fontWeight="medium"
                              lineHeight="tight"
                              _dark={{ color: 'gray.200' }}
                              _light={{ color: 'black' }}
                              whiteSpace="nowrap"
                              textDecoration={isDisabled ? 'line-through' : 'none'}
                            >
                              {s.name}
                            </FaceliftText>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </FaceliftScrollContainer>
          </Box>
        </Box>
      </Show>
    </>
  );
};

export default GroupedBarChart;


